import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { googleAnalyticsService, GA4Account, GA4Property } from '../../services/googleAnalytics';
import { toast } from 'sonner';
import { 
  BarChart3, 
  Building, 
  Globe, 
  ChevronDown, 
  CheckCircle, 
  ExternalLink,
  RefreshCw,
  AlertCircle,
  Loader
} from 'lucide-react';

interface GA4AccountSelectorProps {
  onSelectionChange: (selection: {
    accountId: string;
    accountName: string;
    propertyId: string;
    propertyName: string;
  } | null) => void;
  isRequired?: boolean;
  showSkipOption?: boolean;
  onSkip?: () => void;
}

export function GA4AccountSelector({ 
  onSelectionChange, 
  isRequired = false,
  showSkipOption = false,
  onSkip 
}: GA4AccountSelectorProps) {
  const [accounts, setAccounts] = useState<GA4Account[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [selectedProperty, setSelectedProperty] = useState<string>('');
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    checkConnectionAndLoadAccounts();
  }, []);

  useEffect(() => {
    // Update parent when selection changes
    if (selectedAccount && selectedProperty) {
      const account = accounts.find(acc => acc.accountId === selectedAccount);
      const property = account?.properties.find(prop => prop.propertyId === selectedProperty);
      
      if (account && property) {
        onSelectionChange({
          accountId: account.accountId,
          accountName: account.accountName,
          propertyId: property.propertyId,
          propertyName: property.propertyName
        });
      }
    } else {
      onSelectionChange(null);
    }
  }, [selectedAccount, selectedProperty, accounts, onSelectionChange]);

  const checkConnectionAndLoadAccounts = async () => {
    try {
      // Check if OAuth was just completed from project creation
      const oauthCompleted = sessionStorage.getItem('ga4_oauth_completed');
      if (oauthCompleted === 'true') {
        console.log('OAuth completed from project creation, loading accounts');
        sessionStorage.removeItem('ga4_oauth_completed');
        setIsConnected(true);
        await loadAccounts();
        return;
      }
      
      const hasConnections = await googleAnalyticsService.hasConnections();
      
      if (hasConnections) {
        setIsConnected(true);
        await loadAccounts();
      } else {
        setIsConnected(false);
        setConnectionError(null);
      }
    } catch (error) {
      console.error('Error checking GA4 connection:', error);
      setIsConnected(false);
      setConnectionError('Failed to check GA4 connection status');
    }
  };

  const loadAccounts = async () => {
    setIsLoadingAccounts(true);
    setConnectionError(null);
    
    try {
      const accountsData = await googleAnalyticsService.getAccounts();
      setAccounts(accountsData);
      
      // Auto-select first account/property if only one exists
      if (accountsData.length === 1) {
        setSelectedAccount(accountsData[0].accountId);
        if (accountsData[0].properties.length === 1) {
          setSelectedProperty(accountsData[0].properties[0].propertyId);
        }
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
      setConnectionError('Failed to load Google Analytics accounts. Please try connecting again.');
      setIsConnected(false);
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    setConnectionError(null);
    
    try {
      // Store a flag to indicate we're connecting from project creation
      sessionStorage.setItem('ga4_connecting_from_project_creation', 'true');
      
      const result = await googleAnalyticsService.initiateOAuth();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to initiate OAuth');
      }
      
      // OAuth will redirect, so we don't need to handle success here
    } catch (error) {
      console.error('OAuth initiation error:', error);
      setConnectionError(error instanceof Error ? error.message : 'Failed to connect to Google Analytics');
      setIsConnecting(false);
    }
  };

  const handleRetry = () => {
    setConnectionError(null);
    checkConnectionAndLoadAccounts();
  };

  const getSelectedAccountData = () => {
    return accounts.find(acc => acc.accountId === selectedAccount);
  };

  const getSelectedPropertyData = () => {
    const account = getSelectedAccountData();
    return account?.properties.find(prop => prop.propertyId === selectedProperty);
  };

  // If not connected, show connection interface
  if (!isConnected) {
    return (
      <Card className="p-6 border-2 border-dashed border-gray-300 dark:border-gray-600">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
            <BarChart3 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Connect Google Analytics 4
          </h3>
          
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Connect your GA4 account to enable powerful analytics insights for your project.
          </p>

          {connectionError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                <p className="text-sm text-red-700 dark:text-red-400">{connectionError}</p>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              onClick={handleConnect} 
              disabled={isConnecting}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              {isConnecting ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Connect with Google
                </>
              )}
            </Button>
            
            {connectionError && (
              <Button variant="outline" onClick={handleRetry}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            )}
            
            {showSkipOption && onSkip && (
              <Button variant="ghost" onClick={onSkip}>
                Skip for now
              </Button>
            )}
          </div>

          {!isRequired && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
              You can connect GA4 later from your project settings
            </p>
          )}
        </div>
      </Card>
    );
  }

  // If connected but loading accounts
  if (isLoadingAccounts) {
    return (
      <Card className="p-6">
        <div className="text-center">
          <LoadingSpinner className="w-8 h-8 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading your Google Analytics accounts...</p>
        </div>
      </Card>
    );
  }

  // If connected but no accounts found
  if (accounts.length === 0) {
    return (
      <Card className="p-6 border-2 border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full mb-4">
            <AlertCircle className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No Google Analytics Accounts Found
          </h3>
          
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            We couldn't find any Google Analytics accounts associated with your Google account.
            Please make sure you have GA4 properties set up.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" onClick={handleRetry}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            
            {showSkipOption && onSkip && (
              <Button variant="ghost" onClick={onSkip}>
                Skip for now
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  }

  // Show account and property selection
  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div className="flex items-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full mr-4">
            <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Select Google Analytics Property
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Choose which GA4 property to connect to this project
            </p>
          </div>
        </div>

        {/* Account Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Google Analytics Account
          </label>
          <div className="relative">
            <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
            <select
              value={selectedAccount}
              onChange={(e) => {
                setSelectedAccount(e.target.value);
                setSelectedProperty(''); // Reset property selection
              }}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              required={isRequired}
            >
              <option value="">Select an account...</option>
              {accounts.map((account) => (
                <option key={account.accountId} value={account.accountId}>
                  {account.accountName} ({account.properties.length} properties)
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4 pointer-events-none" />
          </div>
        </div>

        {/* Property Selection */}
        {selectedAccount && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              GA4 Property
            </label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
              <select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                required={isRequired}
              >
                <option value="">Select a property...</option>
                {getSelectedAccountData()?.properties.map((property) => (
                  <option key={property.propertyId} value={property.propertyId}>
                    {property.propertyName}
                    {property.websiteUrl && ` (${property.websiteUrl})`}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4 pointer-events-none" />
            </div>
          </div>
        )}

        {/* Selection Summary */}
        {selectedAccount && selectedProperty && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2">
              Selected Configuration
            </h4>
            <div className="space-y-1 text-sm">
              <div className="flex items-center">
                <Building className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                <span className="text-blue-800 dark:text-blue-300">
                  Account: {getSelectedAccountData()?.accountName}
                </span>
              </div>
              <div className="flex items-center">
                <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                <span className="text-blue-800 dark:text-blue-300">
                  Property: {getSelectedPropertyData()?.propertyName}
                </span>
              </div>
              {getSelectedPropertyData()?.websiteUrl && (
                <div className="flex items-center">
                  <ExternalLink className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                  <span className="text-blue-800 dark:text-blue-300">
                    Website: {getSelectedPropertyData()?.websiteUrl}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
} 